import type { IconComponent } from '@digital-twins/design-system/icons'
import type { ActivityTargetKey, ThreadPlaceholderTemplates } from '@/lib/constants/languages'
import type { AssistantActivitySourceKind } from '@digital-twins/shared-types/assistant-activity'
import { ENTITY_ICONS, studyEntityIcon } from '@/lib/icons/entityIcons'
import type { ThreadActivityLeg } from './ThreadActivityIndicator'
import { ACTIVITY_CHIP_ICONS } from './activityIcons'

const SOURCE_TARGETS: Record<AssistantActivitySourceKind, ActivityTargetKey> = {
  library: 'document_search', social: 'tracker_search', study: 'read_study', room: 'read_room',
  audience: 'read_audience', web: 'web_search', dataset: 'analyze_table', connector_record: 'call_connected_source',
}

/** Shared by the checklist and older chip consumers; one real call stays one row. */
export function activitySourceKeys(leg: ThreadActivityLeg): Array<ActivityTargetKey> {
  return leg.sourceKinds?.length ? leg.sourceKinds.map(kind => SOURCE_TARGETS[kind]) : [leg.tool as ActivityTargetKey]
}

export function activityIcon(leg: ThreadActivityLeg): IconComponent {
  if (leg.subject?.kind === 'study') return studyEntityIcon(leg.subject.goalId)
  if (leg.subject && leg.subject.kind !== 'library') return ENTITY_ICONS[leg.subject.kind]
  if (leg.sourceKinds?.length === 1) return ENTITY_ICONS[leg.sourceKinds[0]]
  if ((leg.sourceKinds?.length ?? 0) > 1) return ENTITY_ICONS.research
  return Object.hasOwn(ACTIVITY_CHIP_ICONS, leg.tool)
    ? ACTIVITY_CHIP_ICONS[leg.tool as ActivityTargetKey] : ENTITY_ICONS.research
}

export function activitySearchLabel(leg: ThreadActivityLeg, copy: ThreadPlaceholderTemplates, language: string): string | null {
  if (!leg.sourceKinds?.length || !['document_search', 'research_search'].includes(leg.tool)) return null
  // Research names every place it looked; the outcome counts only what found something.
  const sources = new Intl.ListFormat(language, { style: 'long', type: 'conjunction' })
    .format(leg.sourceKinds.map(kind => copy.steps.sourceNames[kind]))
  return copy.steps.sourceSearch[leg.done ? 'done' : 'running'].replace('{sources}', sources)
}
